import React, { useState } from 'react';
import { RefreshCw, Search, CalendarPlus, Mail, Files, ChevronDown, ChevronUp } from 'lucide-react';

const PIPELINE_STAGES = [
  {
    key: 'nouveau',
    label: 'Nouveau',
    headerCls: 'bg-sky-100 text-sky-800',
    dotCls: 'bg-sky-400',
    emptyBorder: 'border-sky-200',
    dropCls: 'ring-2 ring-sky-400 bg-sky-50/60',
  },
  {
    key: 'contacte',
    label: 'Contacté',
    headerCls: 'bg-amber-100 text-amber-800',
    dotCls: 'bg-amber-400',
    emptyBorder: 'border-amber-200',
    dropCls: 'ring-2 ring-amber-400 bg-amber-50/60',
  },
  {
    key: 'rdv_planifie',
    label: 'RDV planifié',
    headerCls: 'bg-violet-100 text-violet-800',
    dotCls: 'bg-violet-400',
    emptyBorder: 'border-violet-200',
    dropCls: 'ring-2 ring-violet-400 bg-violet-50/60',
  },
  {
    key: 'converti',
    label: 'Converti',
    headerCls: 'bg-emerald-100 text-emerald-800',
    dotCls: 'bg-emerald-500',
    emptyBorder: 'border-emerald-200',
    dropCls: 'ring-2 ring-emerald-400 bg-emerald-50/60',
  },
  {
    key: 'archive',
    label: 'Archivé',
    headerCls: 'bg-slate-100 text-slate-600',
    dotCls: 'bg-slate-400',
    emptyBorder: 'border-slate-200',
    dropCls: 'ring-2 ring-slate-400 bg-slate-50/60',
    collapsible: true,
  },
];

const fmtDateShort = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch { return iso; }
};

/** Carte prospect — non draggable directement, le wrapper gère le drag */
function ProspectCard({ group, onSelectGroup, onSchedule, isDragging }) {
  const mainRequest = group.requests[0];

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:border-slate-400 hover:shadow-sm transition-all select-none ${
        isDragging ? 'opacity-40 shadow-xl scale-95 rotate-1' : ''
      }`}
      data-testid="papa-request-row"
      onClick={() => onSelectGroup(group)}
    >
      {/* Nom + badge nb demandes */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-semibold text-slate-900 text-sm leading-snug">{group.displayName}</span>
        {group.requests.length > 1 && (
          <span
            className="flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200 flex-shrink-0"
            data-testid="papa-request-count-badge"
          >
            <Files size={9} /> {group.requests.length}
          </span>
        )}
      </div>

      {/* Badges source + version */}
      <div className="flex flex-wrap gap-1 mb-2">
        {[...new Set(group.requests.map((r) => r.source))].map((src) => (
          <span
            key={src}
            className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
              src === 'tunnel' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'
            }`}
          >
            {src === 'tunnel' ? 'Tunnel' : 'Formulaire'}
          </span>
        ))}
        {mainRequest.version && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
            {mainRequest.version}
          </span>
        )}
      </div>

      {/* Email */}
      <p className="flex items-center gap-1 text-xs text-slate-500 truncate mb-2">
        <Mail size={11} className="flex-shrink-0 text-slate-400" />
        {group.email || '—'}
      </p>

      {/* Date + notes + bouton RDV */}
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400">{fmtDateShort(group.latestDate)}</span>
          {group.totalNotes > 0 && (
            <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 font-semibold">
              {group.totalNotes} note{group.totalNotes > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSchedule({
              nom: mainRequest.nom,
              email: mainRequest.email,
              request_ref: `${mainRequest.source}:${mainRequest.id}`,
            });
          }}
          data-testid="papa-request-schedule"
          className="flex items-center gap-1 text-[10px] font-semibold text-white bg-slate-900 hover:bg-slate-700 rounded-lg px-2 py-1 flex-shrink-0 transition-colors"
          title="Planifier un rendez-vous"
        >
          <CalendarPlus size={11} /> RDV
        </button>
      </div>
    </div>
  );
}

export default function DemoRequestsTab({ onSchedule, onSelectGroup, groups, loading, onRefresh, onChangeStatus }) {
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [archiveOpen, setArchiveOpen] = useState(false);

  // État du drag & drop
  const [draggingEmail, setDraggingEmail] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Filtrage
  const filtered = groups.filter((g) => {
    if (sourceFilter !== 'all' && !g.requests.some((r) => r.source === sourceFilter)) return false;
    if (query) {
      const q = query.toLowerCase();
      return g.requests.some((r) => `${r.nom} ${r.email} ${r.societe}`.toLowerCase().includes(q));
    }
    return true;
  });

  const getGroupsForStage = (stageKey) => filtered.filter((g) => g.latestStatus === stageKey);
  const activeCount = filtered.filter((g) => g.latestStatus !== 'archive').length;

  // ── Gestionnaires drag sur les colonnes ──
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, stageKey) => {
    e.preventDefault();
    setDragOverStage(stageKey);
  };

  const handleDragLeave = (e) => {
    // N'effacer que si on quitte réellement la colonne (pas en entrant dans un enfant)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = (e, stageKey) => {
    e.preventDefault();
    const email = e.dataTransfer.getData('text/plain');
    const group = groups.find((g) => g.email === email);
    // Ne rien faire si on dépose dans la même colonne
    if (group && group.latestStatus !== stageKey) {
      onChangeStatus(group.requests[0], stageKey);
    }
    setDragOverStage(null);
    setDraggingEmail(null);
  };

  return (
    <div data-testid="papa-requests-tab">

      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher nom, e-mail, société…"
            data-testid="papa-requests-search"
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          data-testid="papa-requests-source-filter"
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white"
        >
          <option value="all">Toutes sources</option>
          <option value="tunnel">Tunnel OTP</option>
          <option value="formulaire">Formulaire</option>
        </select>
        <button
          onClick={onRefresh}
          data-testid="papa-requests-refresh"
          className="flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-2 bg-white hover:bg-slate-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
        </button>
      </div>

      <p className="text-xs text-slate-500 mb-4" data-testid="papa-requests-count">
        {activeCount} prospect{activeCount > 1 ? 's' : ''} actif{activeCount > 1 ? 's' : ''}
      </p>

      {/* ── Pipeline Kanban ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 pb-6" style={{ minHeight: '65vh' }}>
        {PIPELINE_STAGES.map((stage) => {
          const stageGroups = getGroupsForStage(stage.key);
          const isArchive = stage.collapsible === true;
          const isOpen = !isArchive || archiveOpen;
          const isDragTarget = dragOverStage === stage.key;

          return (
            <div
              key={stage.key}
              className={`flex flex-col rounded-2xl p-2 transition-all duration-150 ${
                isDragTarget ? stage.dropCls : 'bg-transparent'
              }`}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, stage.key)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.key)}
            >
              {/* En-tête de colonne */}
              <div className={`flex items-center justify-between px-3 py-2 rounded-xl mb-3 ${stage.headerCls}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stage.dotCls}`} />
                  <span className="text-sm font-bold">{stage.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="bg-white/60 text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {stageGroups.length}
                  </span>
                  {isArchive && (
                    <button
                      onClick={() => setArchiveOpen((o) => !o)}
                      className="opacity-60 hover:opacity-100 transition-opacity"
                      title={archiveOpen ? 'Réduire' : 'Afficher les archivés'}
                    >
                      {archiveOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  )}
                </div>
              </div>

              {/* Cartes de la colonne */}
              {isOpen && (
                <div className="flex flex-col gap-2 flex-1 min-h-[60px]">
                  {stageGroups.map((g) => (
                    <div
                      key={g.email || g.requests[0].id}
                      draggable={true}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', g.email);
                        // Délai léger pour que le ghost soit rendu avant l'effet
                        setTimeout(() => setDraggingEmail(g.email), 0);
                      }}
                      onDragEnd={() => {
                        setDraggingEmail(null);
                        setDragOverStage(null);
                      }}
                    >
                      <ProspectCard
                        group={g}
                        onSelectGroup={onSelectGroup}
                        onSchedule={onSchedule}
                        isDragging={draggingEmail === g.email}
                      />
                    </div>
                  ))}

                  {/* Zone de dépôt vide */}
                  {stageGroups.length === 0 && (
                    <div
                      className={`flex items-center justify-center h-16 rounded-xl border border-dashed text-xs transition-colors ${
                        isDragTarget
                          ? 'border-current text-slate-500 font-semibold'
                          : `${stage.emptyBorder} text-slate-300`
                      }`}
                    >
                      {isDragTarget ? '↓ Déposer ici' : 'Aucun prospect'}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
